import { EditOutlined } from "@ant-design/icons";
import { AgGridReact } from "ag-grid-react";
import { Button, Divider } from "antd";
import CiqAgSearch from "components/ciq-ag-search";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";

import { ColumnMovedEvent, ColumnVisibleEvent } from "ag-grid-enterprise";
import {
  isPermissionNotGrantted,
  ProjectContext
} from "context/ProjectProvider";
import {
  FilterChipComponent,
  FilterChipComponentRef
} from "entity-app/shared-components/log-render-components/filter-chip-component";
import {
  calculateCustomReportStats,
  getGridStatusBudge
} from "components/ciq-log-page-header/grid-status-budge";
import { useLocationBreakdownV2Structure } from "hooks/location-breakdown-v2-structure";
import { ProjectPermissionEnum } from "constants/index";
import { FilterItem } from "models/types";
import { GridLoadingIndicator } from "components/widgets";
import { FeatureTypes } from "entity-app/constants";
import RenameColumnsModal from "./rename-columns-modal";
import {
  useCustomReports,
  useCustomReportsDispatch
} from "../context/custom-reports-provider";
import {
  applyUnassignedDefaults,
  flattenData,
  pairStringsInArray,
  flattenJSON1ToJSON2,
  reorderJSON1FromJSON2,
  translateVLargeMaterial,
  updateColumnVisibility
} from "../utils/utils";
import useReportColumnDef from "../hooks/report-column-def";
import DatasetConfigTags from "./dataset-config-tags";
import { getCustomReportData } from "../services/custom-report-services";

type Props = {
  sidebarState: any[];
  onUpdateColumnConfig: (config: any) => Promise<void>;
  onUpdateProjectReport: (data: any) => Promise<void>;
  projectId: string;
  reportId: string;
};

const columnsToIgnore = [
  "description",
  "quantity_unit_id",
  "material_size_unit"
];

function ResultsPanel(props: Props) {
  const {
    sidebarState,
    onUpdateColumnConfig,
    onUpdateProjectReport,
    projectId,
    reportId
  } = props;
  const { projectDetails, tokenContents, projectToken } =
    useContext(ProjectContext);
  const { datasetConfig, gridState, columnConfig, permissions } =
    useCustomReports();
  const dispatch: any = useCustomReportsDispatch();
  const [isSidebarOpen, setIsSidebarOpen] = sidebarState;
  const gridRef = useRef<AgGridReact>(null);
  const filterChipRef = useRef<FilterChipComponentRef>(null);
  const [isGridReady, setIsGridReady] = useState(false);
  const [isOpenRenameColumnsModal, setIsOpenRenameColumnsModal] =
    useState(false);
  const [gridFilterItems, setGridFilterItems] = useState<FilterItem[]>([]);
  const [isFirstDataRendered, setFirstDataRendered] = useState(false);

  const [rowGroupExpandedState, setRowGroupExpandedState] = useState<{
    isAllExpanded: boolean;
    isAllCollapsed: boolean;
  }>({
    isAllExpanded: false,
    isAllCollapsed: true
  });

  const [vLargeMaterial, setVLargeMaterial] = useState<any[] | null>(null);
  const [isVLargeMaterialLoading, setIsVLargeMaterialLoading] = useState(false);

  const getReportData = useCallback(async () => {
    if (!reportId || !projectToken) return;

    const response = await getCustomReportData(reportId, projectToken);
    setVLargeMaterial(response?.data || null);
    setIsVLargeMaterialLoading(false);
  }, [reportId, projectToken]);

  useEffect(() => {
    getReportData();
  }, [getReportData]);

  const materials = useMemo(() => {
    if (!vLargeMaterial) return null;
    return translateVLargeMaterial(vLargeMaterial);
  }, [vLargeMaterial]);

  const rowData = useMemo(() => {
    if (!datasetConfig?.linkedEntity || !materials || isVLargeMaterialLoading)
      return null;

    let opMaterials = [...materials];

    const inlineModeForFeatures = datasetConfig?.linkedEntity
      .filter((entity) => entity.display_type === "inline")
      .reduce((acc, entity) => {
        acc.push(entity.feature_type_id);
        return acc;
      }, [] as number[]);

    opMaterials = applyUnassignedDefaults(
      opMaterials,
      datasetConfig?.groupByField,
      columnConfig
    );

    opMaterials = flattenData(opMaterials, inlineModeForFeatures);

    return opMaterials;
  }, [
    columnConfig,
    datasetConfig?.groupByField,
    datasetConfig?.linkedEntity,
    isVLargeMaterialLoading,
    materials
  ]);

  const enableCellSpan = useMemo(() => {
    const hasInlineDisplayType = datasetConfig?.linkedEntity.some(
      (entity) => entity.display_type === "inline"
    );
    return !!hasInlineDisplayType;
  }, [datasetConfig?.linkedEntity]);

  const canSetLBSForMaterial = !isPermissionNotGrantted(
    ProjectPermissionEnum.CanSetLBSForMaterial,
    tokenContents?.role!
  );

  const { isLBSFeatureEnabled, handleOnFilterOpen } =
    useLocationBreakdownV2Structure({
      entityList: rowData || [],
      projectId: projectDetails?.id!,
      gridRef,
      hasPermissionToSetLBSForMaterial: canSetLBSForMaterial,
      locationDataKey: "project_locations"
    });

  const { columnDefs, defaultColDef, autoGroupColumnDef, sideBarConfig } =
    useReportColumnDef({
      linkedEntityDisplayType: datasetConfig?.linkedEntity ?? [],
      groupByField: datasetConfig?.groupByField ?? "none",
      isLBSFeatureEnabled,
      projectId
    });

  const onFirstDataRendered = () => {
    gridRef.current?.api.setFilterModel(gridState?.filterModel || null);
    setFirstDataRendered(true);
  };

  const saveFilterChange = useCallback(async () => {
    const filterModel = gridRef.current?.api.getFilterModel();

    const calcStats = calculateCustomReportStats({
      gridRef
    });
    dispatch({
      type: "UPDATE_GRID_STATS_BADGE",
      payload: getGridStatusBudge({
        title: "Report",
        totalCount: materials?.length,
        filterStats: calcStats
      })
    });
    // return;
    await onUpdateProjectReport({ filters_config: filterModel });
  }, [dispatch, materials?.length, onUpdateProjectReport, gridRef]);

  const onFilterChanged = useCallback(
    async (e: any) => {
      filterChipRef.current?.onFilterApplied();
      if (e.source === "api") return;

      dispatch({
        type: "UPDATE_GRID_STATE",
        payload: {
          filterModel: e.api.getFilterModel()
        }
      });

      saveFilterChange();
    },
    [dispatch, saveFilterChange]
  );

  const onColumnVisible = useCallback(
    async (e: ColumnVisibleEvent) => {
      // console.log("onColumnVisible: ", e);
      if (e.source === "api") return;

      const fieldKey = e.column?.getColId();
      if (!fieldKey || !columnConfig || !isFirstDataRendered) return;

      const updated = updateColumnVisibility(
        columnConfig,
        fieldKey,
        e.column?.isVisible() ?? false
      );

      dispatch({
        type: "UPDATE_COLUMN_CONFIG",
        payload: updated
      });
      // return;
      await onUpdateProjectReport({ columns_config: updated });
    },
    [columnConfig, dispatch, isFirstDataRendered, onUpdateProjectReport]
  );

  const onColumnMoved = useCallback(
    async (e: ColumnMovedEvent) => {
      if (!columnConfig || !isFirstDataRendered || e.source === "api") return;
      const allCols = e.api.getAllGridColumns();
      const orderedKeys = allCols.map((c) => c.getColId());

      const updatedOrderedKeys = orderedKeys.map((key) =>
        key === "ag-Grid-AutoColumn" ? "group_by_column" : key
      );

      const pairedOrderedKeys = pairStringsInArray({
        startString: "title",
        pairs: ["description"],
        inputArray: updatedOrderedKeys
      });

      const updated = reorderJSON1FromJSON2(
        columnConfig,
        pairedOrderedKeys,
        datasetConfig?.linkedEntity[0]?.display_type
      );
      dispatch({
        type: "UPDATE_COLUMN_CONFIG",
        payload: updated
      });

      await onUpdateProjectReport({ columns_config: updated });
    },
    [
      columnConfig,
      datasetConfig?.linkedEntity,
      dispatch,
      isFirstDataRendered,
      onUpdateProjectReport
    ]
  );

  const onColumnSorted = useCallback(
    async (e: any) => {
      if (e.source === "api") return;

      const colState = e.api.getColumnState();
      const sortedColumns = colState.filter((col: any) => col.sort);
      sortedColumns.sort((a: any, b: any) => a.sortIndex - b.sortIndex);
      const sortConfig = sortedColumns.map((col: any) => ({
        field_key:
          col.colId === "ag-Grid-AutoColumn" ? "group_by_column" : col.colId,
        sort: col.sort
      }));

      dispatch({
        type: "UPDATE_GRID_STATE",
        payload: {
          sortState: sortConfig
        }
      });
      // return;
      await onUpdateProjectReport({ sorts_config: sortConfig });
    },
    [dispatch, onUpdateProjectReport]
  );

  const onGridFilterOpened = useCallback(
    (event: any) => {
      if (event.column.colId === "project_locations") {
        handleOnFilterOpen(event);
      }
    },
    [handleOnFilterOpen]
  );

  const onGridReady = useCallback(
    (e: any) => {
      setIsGridReady(true);

      if (!columnConfig) return;

      const flattened = flattenJSON1ToJSON2(
        columnConfig,
        datasetConfig?.linkedEntity[0]?.display_type
      );

      const columnState = flattened.map((col: any) => {
        const fieldSorting: any = gridState?.sortState?.find(
          (sort: any) => sort.field_key === col.colId
        );

        let { hide } = col;
        const linkedSubmittalColConfig = datasetConfig?.linkedEntity.find(
          (entity) => entity.feature_type_id === FeatureTypes.SUBMITTALS
        );

        const linkedActivityColConfig = datasetConfig?.linkedEntity.find(
          (entity) => entity.feature_type_id === FeatureTypes.ACTIVITIES
        );

        if (col.colId === "linked_submittals") {
          hide = linkedSubmittalColConfig?.display_type === "none";
        }

        if (col.colId === "linked_activities") {
          hide = linkedActivityColConfig?.display_type === "none";
        }

        if (
          datasetConfig?.groupByField !== "none" &&
          col.colId === datasetConfig?.groupByField
        ) {
          hide = true;
        }

        const config: any = {
          colId:
            col.colId === "group_by_column" ? "ag-Grid-AutoColumn" : col.colId,
          hide,
          order: col.order ?? 999 // ordering (lower comes first)
        };

        // console.log("config: ", config);

        if (fieldSorting) {
          const fieldSortIndex = gridState?.sortState?.findIndex(
            (sort: any) => sort.field_key === col.colId
          );
          config.sort = fieldSorting.sort;
          config.sortIndex = fieldSortIndex;
        }

        return config;
      });

      // Sort by order for applying
      const cleanedColumnState = columnState
        .sort((a, b) => a.order - b.order)
        .filter((col) => !columnsToIgnore.includes(col.colId));

      e.api.applyColumnState({
        state: cleanedColumnState,
        applyOrder: true
      });
    },
    [
      columnConfig,
      datasetConfig?.groupByField,
      datasetConfig?.linkedEntity,
      gridState?.sortState
    ]
  );

  const isGridLoading = useMemo(() => {
    return !(
      columnConfig &&
      projectDetails &&
      rowData &&
      datasetConfig &&
      !isVLargeMaterialLoading &&
      isGridReady
    );
  }, [
    columnConfig,
    projectDetails,
    rowData,
    datasetConfig,
    isVLargeMaterialLoading,
    isGridReady
  ]);

  const gridKey = useMemo(() => {
    return `${datasetConfig?.linkedEntity[0]?.display_type}_${datasetConfig?.linkedEntity[1]?.display_type}_${datasetConfig?.groupByField}`;
  }, [datasetConfig?.groupByField, datasetConfig?.linkedEntity]);

  const onGridPreDestroyed = useCallback(() => {
    setIsGridReady(false);
    setFirstDataRendered(false);
  }, [setIsGridReady, setFirstDataRendered]);

  const gridStyleConfig = useMemo(() => {
    const isAnyCondensed = datasetConfig?.linkedEntity?.some(
      (entity) => entity.display_type === "condensed"
    );
    return {
      rowHeight: isAnyCondensed ? 62 : 50
    };
  }, [datasetConfig?.linkedEntity]);

  const onExpandOrCollapseAll = useCallback((params: any) => {
    setRowGroupExpandedState({
      isAllExpanded: params.source === "expandAll",
      isAllCollapsed: params.source === "collapseAll"
    });
  }, []);

  const onRowGroupOpened = useCallback(
    (params: any) => {
      let isAllExpanded = true;
      let isAllCollapsed = true;
      params.api.forEachNode((node: any) => {
        if (node.rowGroupIndex !== undefined) {
          isAllExpanded = isAllExpanded && node.expanded;
          isAllCollapsed = isAllCollapsed && !node.expanded;
        }
      });
      setRowGroupExpandedState({
        isAllExpanded,
        isAllCollapsed
      });
    },
    [setRowGroupExpandedState]
  );

  const onExpandAll = useCallback(() => {
    gridRef.current?.api.expandAll();
  }, [gridRef]);

  const onCollapseAll = useCallback(() => {
    gridRef.current?.api.collapseAll();
  }, [gridRef]);

  useEffect(() => {
    setRowGroupExpandedState({
      isAllExpanded: false,
      isAllCollapsed: true
    });
  }, [datasetConfig?.groupByField]);

  return (
    <div
      className="flex flex-col w-full h-full bg-ciq-gray-600 bg-opacity-50"
      data-testid="custom-report-results-panel"
    >
      <div
        className="px-6 py-2 w-full flex space-x-2 items-center justify-between"
        data-testid="custom-report-results-header"
      >
        <div
          className="flex items-center"
          data-testid="custom-report-results-left"
        >
          {!isSidebarOpen && (
            <div className="flex space-x-2 items-center">
              <div className="font-semibold">Dataset:</div>

              <DatasetConfigTags datasetConfig={datasetConfig} />

              {permissions.canConfigureCustomReports && (
                <Button
                  size="small"
                  className="flex items-center rounded-md bg-ciq-primary-900 hover:bg-ciq-primary-800 focus:bg-ciq-primary-800 active:bg-ciq-primary-800 cursor-pointer px-2 py-1"
                  onClick={() => setIsSidebarOpen(true)}
                  data-testid="custom-report-edit-dataset"
                >
                  <EditOutlined className="text-white hover:text-white font-medium" />
                </Button>
              )}
            </div>
          )}
        </div>
        <div
          className="flex space-x-2 items-center"
          data-testid="custom-report-results-right"
        >
          <div data-testid="custom-report-filter-chip">
            <FilterChipComponent
              key="CustomReport_FilterChip"
              gridRef={gridRef}
              ref={filterChipRef}
              onFilterCleared={saveFilterChange}
              setParentFilterItems={setGridFilterItems}
            />
          </div>

          {gridFilterItems.length > 0 && (
            <Divider
              type="vertical"
              className="border-black border-opacity-20 h-8"
            />
          )}

          {datasetConfig?.groupByField !== "none" &&
            !rowGroupExpandedState.isAllExpanded && (
              <Button
                onClick={onExpandAll}
                className="w-28"
                data-testid="custom-report-expand-all"
              >
                Expand All
              </Button>
            )}
          {datasetConfig?.groupByField !== "none" &&
            rowGroupExpandedState.isAllExpanded && (
              <Button
                onClick={onCollapseAll}
                className="w-28"
                data-testid="custom-report-collapse-all"
              >
                Collapse All
              </Button>
            )}

          {permissions.canConfigureCustomReports && (
            <Button
              onClick={() => {
                setIsOpenRenameColumnsModal(!isOpenRenameColumnsModal);
              }}
              data-testid="custom-report-rename-columns-button"
            >
              Rename Columns
            </Button>
          )}
          <div data-testid="custom-report-search">
            <CiqAgSearch
              key="ReportPage_search"
              gridRef={gridRef}
              placeholder="Search Report"
            />
          </div>
        </div>
      </div>
      <div
        className="custom-report-page-results-table flex-grow relative ag-theme-alpine overflow-auto bg-white "
        data-testid="custom-report-grid-container"
      >
        {datasetConfig && (
          <AgGridReact
            key={gridKey}
            ref={gridRef}
            rowData={rowData}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            autoGroupColumnDef={autoGroupColumnDef}
            overlayNoRowsTemplate="No Data Found"
            // headerHeight={36}
            rowHeight={gridStyleConfig.rowHeight}
            enableCellSpan={enableCellSpan}
            groupSuppressBlankHeader
            suppressContextMenu
            sideBar={sideBarConfig}
            loading={isGridLoading}
            context={{ projectDetails, columnConfig }}
            onGridReady={onGridReady}
            onFilterChanged={onFilterChanged}
            onColumnVisible={onColumnVisible}
            onColumnMoved={onColumnMoved}
            onFirstDataRendered={onFirstDataRendered}
            onSortChanged={onColumnSorted}
            maintainColumnOrder
            onFilterOpened={onGridFilterOpened}
            animateRows={false}
            onGridPreDestroyed={onGridPreDestroyed}
            suppressMoveWhenColumnDragging
            loadingOverlayComponent={GridLoadingIndicator}
            onExpandOrCollapseAll={onExpandOrCollapseAll}
            onRowGroupOpened={onRowGroupOpened}
            tooltipShowDelay={0}
          />
        )}
      </div>
      {isOpenRenameColumnsModal && (
        <RenameColumnsModal
          isOpen={isOpenRenameColumnsModal}
          setIsOpen={setIsOpenRenameColumnsModal}
          onUpdateColumnConfig={async (con) => {
            gridRef.current?.api.setGridOption("loading", true);
            await onUpdateColumnConfig(con);
            setTimeout(() => {
              gridRef.current?.api.refreshHeader();
              gridRef.current?.api.refreshToolPanel();
              gridRef.current?.api.setGridOption("loading", false);
            }, 500);
          }}
        />
      )}
    </div>
  );
}

export default ResultsPanel;
